import _ from 'lodash';
import { getRequiredErrorMessage, shouldShowField, validateField as validateJsonField } from '../../../../../components/InputField/helper';
import { validatePincodeByCountry } from '../../../components/PincodeField/helper';

const NAME_ONLY_REGEX = /^[a-zA-Z\s]*$/;
const ADDRESS_REGEX = /^[a-zA-Z0-9#]+[,. # a-zA-Z0-9\/-]*$/;
const PHONE_REGEX = /^[\d\-\(\)\s]+$/;
const SSN_REGEX = /^(?!000|666)[0-8][0-9]{2}(?:-)?(?!00)[0-9]{2}(?:-)?(?!0000)[0-9]{4}$/;

const normalizeFieldPath = (field = "") => String(field).replace(/\[\d+\]/g, "[]");
const getFieldSegment = (field = "") => normalizeFieldPath(field).split('.').pop();
const getFallbackLabel = (field = "") => {
    const segment = getFieldSegment(field);
    if (segment === 'pinCode') return 'Zip Code';
    if (segment === 'dob') return 'Date of Birth';
    return _.startCase(segment);
};
const getFieldLabel = (field, jsonField) => _.startCase(jsonField?.label || getFallbackLabel(field));

const getDefaultRequiredMessage = (field, jsonField) => {
    const fieldSegment = getFieldSegment(field);
    const label = getFieldLabel(field, jsonField);

    if (['gender', 'nationality', 'country'].includes(fieldSegment)) {
        return getRequiredErrorMessage(jsonField, `Please select ${label}.`);
    }

    if (fieldSegment === 'dob') {
        return getRequiredErrorMessage(jsonField, `Please select a valid ${label}.`);
    }

    return getRequiredErrorMessage(jsonField, `Please enter ${label}.`);
};

const getDefaultInvalidMessage = (field, jsonField) => {
    const fieldSegment = getFieldSegment(field);

    if (jsonField?.errorTypes?.format) {
        return jsonField.errorTypes.format;
    }

    if (jsonField?.error) {
        return jsonField.error;
    }

    if (fieldSegment === 'pinCode') {
        return 'Please enter a valid Zip code.';
    }

    return getDefaultRequiredMessage(field, jsonField);
};

const isNameField = (field = "") => ['firstName', 'middleName', 'lastName', 'signerFirstName', 'signerLastName'].includes(getFieldSegment(field));

export const hasValue = (value) => {
    if (value === undefined || value === null) {
        return false;
    }

    if (typeof value === 'string') {
        return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
        return value.length > 0;
    }

    return true;
};

export const validatePhoneNumber = (phone) => PHONE_REGEX.test(phone || "");
export const validateSSN = (ssn) => SSN_REGEX.test(ssn || "");

export const handleValidation = (field, value, jsonField) => {
    const normalizedField = normalizeFieldPath(field);
    const fieldSegment = getFieldSegment(field);
    const label = getFieldLabel(field, jsonField);

    if (isNameField(field)) {
        const isMiddleName = fieldSegment === 'middleName';
        if (!hasValue(value)) {
            return isMiddleName ? "" : getDefaultRequiredMessage(field, jsonField);
        }

        const trimmedValue = String(value).trim();
        if (isMiddleName) {
            if (trimmedValue.length > 50) {
                return `Your ${label} should be less than 50 characters.`;
            }
        } else if (trimmedValue.length < 2 || trimmedValue.length > 50) {
            return `Your ${label} should be between 2 and 50 characters.`;
        }

        if (!NAME_ONLY_REGEX.test(trimmedValue)) {
            return getDefaultInvalidMessage(field, jsonField);
        }

        if (trimmedValue.split(/\s+/).some((word) => word.length > 50)) {
            return `Each word in the ${label} should be less than 50 characters.`;
        }

        return "";
    }

    if (fieldSegment === 'cyclosUserName' && !hasValue(value)) {
        return getDefaultRequiredMessage(field, jsonField);
    }

    if (fieldSegment === 'mobilePhone') {
        if (!hasValue(value)) {
            return getDefaultRequiredMessage(field, jsonField);
        }
        return validatePhoneNumber(value) ? "" : getDefaultInvalidMessage(field, jsonField);
    }

    if (normalizedField.endsWith('address.street') || normalizedField.endsWith('address.buildingNumber')) {
        if (!hasValue(value)) {
            return getDefaultRequiredMessage(field, jsonField);
        }
        return ADDRESS_REGEX.test(String(value).trim()) ? "" : getDefaultInvalidMessage(field, jsonField);
    }

    if (!hasValue(value)) {
        return getDefaultRequiredMessage(field, jsonField);
    }

    return "";
};

export const validateUserDetailsField = (jsonField, value, dataObj) => {
    const field = jsonField?.field;
    if (!field) {
        return "";
    }

    const customError = handleValidation(field, value, jsonField);
    if (customError) {
        return customError;
    }

    if (normalizeFieldPath(field).endsWith('address.pinCode') && hasValue(value) && !validatePincodeByCountry(dataObj?.address?.country, value)) {
        return getDefaultInvalidMessage(field, jsonField);
    }

    if (getFieldSegment(field) === 'ssn' && hasValue(value) && !validateSSN(value)) {
        return getDefaultInvalidMessage(field, jsonField);
    }

    return validateJsonField(jsonField, value, dataObj);
};

const getRequiredFieldError = ({ field, fieldType, fieldMap }) => {
    const jsonField = fieldMap?.[field];
    const label = getFieldLabel(field, jsonField);
    let fallbackMessage = `Please enter ${label}.`;

    if (fieldType === 'checkbox' || jsonField?.type === 'boolean' || jsonField?.uiType === 'radioBoolean') {
        fallbackMessage = label.endsWith('?') ? label : `Please answer ${label}.`;
        return getRequiredErrorMessage(jsonField, fallbackMessage);
    }

    if (jsonField?.uiType === 'checkboxMultiple' || jsonField?.type === 'checkbox' || jsonField?.type === 'multipleSelection') {
        fallbackMessage = `Please select ${label}.`;
        return getRequiredErrorMessage(jsonField, fallbackMessage);
    }

    if (jsonField?.uiType === 'select' || jsonField?.type === 'singleSelection') {
        fallbackMessage = `Please select ${label}.`;
        return getRequiredErrorMessage(jsonField, fallbackMessage);
    }

    return getRequiredErrorMessage(jsonField, fallbackMessage);
};

export const validateRequiredFields = (fields, data, fieldMap = {}) => {
    const errors = [];
    fields.forEach(({ field, fieldType }) => {
        const fieldValue = _.get(data, field);
        const jsonField = fieldMap?.[field];
        const isEmpty = (
            fieldValue === undefined
            || fieldValue === null
            || fieldValue === ""
            || (Array.isArray(fieldValue) && fieldValue.length === 0)
        );

        if (isEmpty) {
            if (fieldType === 'checkbox' && ![false, true].includes(fieldValue)) {
                errors.push(getRequiredFieldError({ field, fieldType, fieldMap }));
                return;
            }

            if (fieldType !== 'checkbox') {
                errors.push(getRequiredFieldError({ field, fieldType, fieldMap }));
            }
            return;
        }

        if (fieldType !== 'checkbox' && jsonField) {
            const validationError = validateJsonField(jsonField, fieldValue, data);
            if (validationError) {
                errors.push(validationError);
            }
        }
    });
    return errors;
};

export const buildUserDetailsFieldOrder = ({
    includePinCode = true,
    includeUsername = true,
    includeTaxId = true,
    includeSSN = false,
    streetBeforeBuilding = true,
} = {}) => {
    const orderedFields = ['firstName', 'middleName', 'lastName'];

    if (streetBeforeBuilding) {
        orderedFields.push('address.street', 'address.buildingNumber');
    } else {
        orderedFields.push('address.buildingNumber', 'address.street');
    }

    orderedFields.push('address.city', 'address.state');

    if (includePinCode) {
        orderedFields.push('address.pinCode');
    }

    orderedFields.push('address.country', 'mobilePhone');

    if (includeUsername) {
        orderedFields.push('cyclosUserName');
    }

    orderedFields.push('gender', 'nationality', 'dob');

    if (includeTaxId) {
        orderedFields.push('taxId');
    }

    if (includeSSN) {
        orderedFields.push('ssn');
    }

    orderedFields.push('Mother_Maiden_Name');

    return orderedFields;
};

export const getVisibleWizardFieldOrder = ({
    fields = [],
    wizard,
    data,
    skipField = () => false,
} = {}) => (
    fields
        .filter((field) => (
            field?.wizard === wizard
            && !skipField(field)
            && shouldShowField(data, field?.showIf)
        ))
        .map((field) => field.field)
);
